/*
 * This utility is used to check that features are accessible to the current
 * user based on their current facility and assigned roles. This ties into
 * the access control API layer on the server.
 */

import AccessService from "Services/AccessService";

export { AccessTypes, FacilitySettings } from "Constants";

export default class Access {

    /** 
    * Use this to automatically update the passed in boolean observable with the
    * result of the authorization check.
    * @deprecated Use FeatureToggleService.checkFeatureToggle instead for all LaunchDarkley toggles
    * for Users/roles checking - may be replaced with a new method
    **/
    static VerifyKO(accessType: string, observable: KnockoutObservable<boolean>) {
        AccessService.authorize(accessType).then(value => {
            observable(value);
        }).error(e => {
            // On error, always return false
            observable(false);
        });
    }

    /**
    * Can be used 3 ways:
    * 1. Verify(type, authorized-function)
    * 2. Verify(type, authorized-function, unauthorized-function)
    * 3. Verify(type).then(authorized => { do stuff with authorized });
    * 
    * "type" should be supplied by using Constants.AccessTypes
    *  
    * @deprecated Use FeatureToggleService.checkFeatureToggle instead for all Launchdarkly toggles
    * for Users/roles checking - may be replaced with a new method
     **/
    static Verify(accessType: string, authorized?: Function, unauthorized?: Function) {
        return AccessService.authorize(accessType).then(canAccess => {

            // Fire callbacks and also return a promise to be optionally used
            Access.handleResults(canAccess, authorized, unauthorized);
            return canAccess;

        }).catch(e => {
            // On error, always return false
            if (unauthorized) {
                unauthorized();
            }

            return false;
        });
    }

    /**
    * Allows checking multiple permissions at once
    * Can handle Knockout observable or callback functions (or both)
    * @deprecated Use FeatureToggleService.checkFeatureToggle instead for all Launchdarkly toggles
    * for Users/roles checking - may be replaced with a new method
    **/
    static VerifyAll(accessList: { accessType: string; observable?: KnockoutObservable<boolean>; authorized?: Function; unauthorized?: Function }[]) {
        const dict: { [accessType: string]: { observable?: KnockoutObservable<boolean>; authorized?: Function; unauthorized?: Function } } = {};

        // Create dictionary to get the callbacks based on access type
        accessList.forEach(access => {
            dict[access.accessType] = {
                observable: access.observable,
                authorized: access.authorized,
                unauthorized: access.unauthorized
            };
        });

        // Create a list we can pass into the API for verification
        const accessTypeList: string[] = accessList.map((access: { accessType: string; observable?: KnockoutObservable<boolean>; authorized?: Function; unauthorized?: Function }) => access.accessType);

        // Loop through returned list and fire off callbacks based on whether or not they
        // have access to each feature
        return AccessService.authorizeAll(accessTypeList).then(accessResults => {
            accessResults.forEach(result => {
                var callbacks = dict[result.accessType];

                if (callbacks.observable) {
                    callbacks.observable(result.authorized);
                }

                if (callbacks.authorized) {
                    Access.handleResults(result.authorized, callbacks.authorized, callbacks.unauthorized);
                }
            });
        });
    }

    /** 
    * Use this to automatically update the passed in boolean observable with the
    * result of the authorization check.
    **/
    static CheckFacilitySettingKO(facilitySetting: string, observable: KnockoutObservable<boolean>) {
        AccessService.checkFacilitySetting(facilitySetting).then(value => {
            observable(value);
        }).error(e => {
            // On error, always return false
            observable(false);
        });
    }

    /** 
    * Can be used 3 ways:
    * 1. CheckFacilitySetting(facilitySetting, authorized-function)
    * 2. CheckFacilitySetting(facilitySetting, authorized-function, unauthorized-function)
    * 3. CheckFacilitySetting(facilitySetting).then(authorized => { do stuff with authorized });
    * 
    * "facilitySetting" should be supplied by using Constants.FacilitySettings
    **/
    static CheckFacilitySetting(facilitySetting: string, authorized?: Function, unauthorized?: Function): Promise<boolean> {
        return AccessService.checkFacilitySetting(facilitySetting).then(canAccess => {

            // Fire callbacks and also return a promise to be optionally used
            Access.handleResults(canAccess, authorized, unauthorized);
            return canAccess;

        }).catch(e => {
            // On error, always return false
            if (unauthorized) {
                unauthorized();
            }

            return false;
        });
    }

    /**
    * Allows checking multiple facility settings at once
    * Can handle Knockout observable or callback functions (or both)
    **/
    static CheckFacilitySettings(facilitySettings: { facilitySetting: string; observable?: KnockoutObservable<boolean>; authorized?: Function; unauthorized?: Function }[]) {
        const dict: { [facilitySetting: string]: { observable?: KnockoutObservable<boolean>; authorized?: Function; unauthorized?: Function } } = {};

        // Create dictionary to get the callbacks based on facility setting
        facilitySettings.forEach(facilitySetting => {
            dict[facilitySetting.facilitySetting] = {
                observable: facilitySetting.observable,
                authorized: facilitySetting.authorized,
                unauthorized: facilitySetting.unauthorized
            };
        });

        // Create a list we can pass into the API for verification
        const facilitySettingList = facilitySettings.map((facilitySetting: { facilitySetting: string; observable?: KnockoutObservable<boolean>; authorized?: Function; unauthorized?: Function }) => { return facilitySetting.facilitySetting });

        // Loop through returned list and fire off callbacks based on whether or not
        // each facility setting is turned on.
        return AccessService.checkFacilitySettings(facilitySettingList).then(results => {
            results.forEach(result => {
                var callbacks = dict[result.facilitySetting];

                if (callbacks.observable) {
                    callbacks.observable(result.authorized);
                }

                if (callbacks.authorized) {
                    Access.handleResults(result.authorized, callbacks.authorized, callbacks.unauthorized);
                }
            });
        });
    }

    /** 
    * Retrieves the text value of a facility setting
    * "facilitySetting" should be supplied by using Constants.FacilitySettings
    **/
    static GetFacilitySetting(facilitySetting: string): Promise<string> {
        return AccessService.getFacilitySetting(facilitySetting);
    }

    // Fires callbacks based on authorization result
    private static handleResults(canAccess: boolean, authorized: Function, unauthorized: Function) {
        if (authorized) {
            if (canAccess) {
                // Call user defined success function
                authorized();
            } else if (unauthorized) {
                // Call user defined failure function if defined
                unauthorized();
            }
        }
    }

}
